"use client";

import * as React from "react";
import { cn } from "@repo/utils/cn";
import { VideoPlayerButton } from "./VideoPlayerButton";
import { AiDisclaimerForAlt } from "./AiDisclaimer";

export interface VideoProps {
  src: string;
  alt?: string;
  className?: string;
  autoplay?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  playsInline?: boolean;
  poster?: string;
  aspectRatio?: string;
  quality?: number;
  borderRadius?: string;
  playButtonImage?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  imageSpace?: "fill" | "fit";
  maxWidth?: number;
  maxHeight?: number;
}

export const Video: React.FC<VideoProps> = ({
  src,
  alt = "",
  className,
  autoplay = false,
  loop = false,
  muted = false,
  controls = true,
  playsInline = true,
  poster,
  aspectRatio = "16/9",
  quality,
  borderRadius,
  playButtonImage,
  imageSpace = "fill",
  maxWidth = 420,
  maxHeight = 420,
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = React.useState(autoplay);

  // Process src with quality if needed (for Storyblok assets)
  const processedSrc = React.useMemo(() => {
    if (quality && src) {
      try {
        const url = new URL(src);
        url.pathname += `/m/${quality}x0`;
        return url.toString();
      } catch {
        return src;
      }
    }
    return src;
  }, [src, quality]);

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.warn("Video play was prevented:", error);
      });
      setIsPlaying(true);
    }
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  React.useEffect(() => {
    if (autoplay && videoRef.current && !isPlaying) {
      videoRef.current.play().catch((error) => {
        console.warn("Autoplay was prevented:", error);
      });
    }
  }, [autoplay, isPlaying]);

  const containerStyle: React.CSSProperties = {
    aspectRatio: aspectRatio,
    ...(borderRadius && { borderRadius }),
  };

  if (aspectRatio === "1/1") {
    containerStyle.maxWidth = `${maxWidth}px`;
    containerStyle.maxHeight = `${maxHeight}px`;
  }

  return (
    <div
      className={cn(
        "uipm:relative uipm:w-full uipm:overflow-hidden",
        className,
      )}
      style={containerStyle}
    >
      <video
        ref={videoRef}
        className={cn(
          "uipm:w-full uipm:h-full",
          imageSpace === "fill" ? "uipm:object-cover" : "uipm:object-contain",
        )}
        loop={loop}
        muted={muted}
        controls={controls && isPlaying}
        playsInline={playsInline}
        poster={poster}
        preload="metadata"
        aria-label={alt}
        onPause={handlePause}
        onPlay={handlePlay}
        style={{
          borderRadius,
        }}
      >
        <source src={processedSrc} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Resolved from the untransformed `src`: the registry is keyed by the asset url,
          and `processedSrc` may carry a quality transform. A video has no alt field
          here, so the registry is the only thing that can label it. */}
      <AiDisclaimerForAlt alt={alt || null} src={src} />

      {!isPlaying && (
        <VideoPlayerButton
          onClick={handlePlayClick}
          playButtonImage={playButtonImage}
        />
      )}
    </div>
  );
};
